import { Component, type ErrorInfo, type ReactNode } from 'react'

import { ErrorPanel } from './ErrorPanel'

interface Props {
  children: ReactNode
  /** Rendered instead of the default panel, for route-level boundaries. */
  fallback?: (reset: () => void, reference: string) => ReactNode
}

interface State {
  error: Error | null
  reference: string
}

/** A short reference the operator can quote, matching what was logged. */
function newReference(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase()
}

/**
 * Catches a render-time crash so one broken screen does not blank the console.
 *
 * React unmounts the whole tree on an uncaught error, which to an operator
 * looks exactly like the service being down.
 *
 * The operator is deliberately never shown the exception. This boundary used
 * to render `error.message`, which is how "A rial amount must be an integer,
 * received null" appeared on a Staging screen — a sentence that is precise,
 * useful, and addressed entirely to a developer. The message, the stack and
 * the component trace go to the console with a reference; the operator gets
 * the reference and something they can act on.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, reference: '' }

  static getDerivedStateFromError(error: Error): State {
    return { error, reference: newReference() }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Everything technical, in one place, tagged with what the operator sees.
    console.error(
      `[client-portal] render error ref=${this.state.reference}`,
      error,
      info.componentStack,
    )
  }

  private reset = () => this.setState({ error: null, reference: '' })

  override render(): ReactNode {
    const { error, reference } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(this.reset, reference)
    return <ErrorPanel reference={reference} onRetry={this.reset} />
  }
}
